/**
 * Pure helpers for the add-line "confirm price" step (kanban e271f10d).
 *
 * The lookup endpoint (`GET /portal/products/lookup/{item}?quantity=N`)
 * returns the waterfall's authoritative price PLUS the backend-classified
 * `current_source` / `available_sources` for the item. These helpers turn
 * that into what the rep sees before clicking Add — "SP $144.95 (this is
 * what you'll get), Quote #00123 $140.00, Std $702.71" — and into the
 * post-add success line. Kept in a sibling .ts so they unit-test without
 * rendering the 7k-line modal.
 */

import type { ResolvedPriceInfo } from '../../services/portalProducts';
import type {
  LineItemPricingContext,
  PricingSourceOption,
  PricingSourceType,
} from '../../services/portalOrders';

/** One selectable price on the confirm panel. */
export interface PriceChoice {
  key: string;
  type: PricingSourceType;
  label: string;
  sublabel: string | null;
  price: number;
  /** The waterfall's answer — what Add uses unless the rep picks another. */
  recommended: boolean;
}

/**
 * Human label for the waterfall's own source when it matched none of the
 * listed options (base-discount pricing, a hybrid/catalog price the option
 * list could not see, …). The rep still needs to know where it came from.
 */
export const resolvedSourceLabel = (resolved: ResolvedPriceInfo): string => {
  switch (resolved.source) {
    case 'special_pricing':
      return 'Special Pricing';
    case 'special_pricing_base_discount':
      return 'Special Pricing — base discount';
    case 'customer_quote':
      return resolved.quote_id != null
        ? `Quote #${String(resolved.quote_id).padStart(5, '0')}`
        : 'Customer quote';
    case 'product_hybrid':
      return 'Standard Price';
    default:
      return 'Resolved price';
  }
};

const sourceTypeOf = (source: string): PricingSourceType => {
  switch (source) {
    case 'special_pricing':
      return 'special';
    case 'special_pricing_base_discount':
      return 'base_discount';
    case 'customer_quote':
      return 'quote';
    case 'product_hybrid':
      return 'standard';
    default:
      return 'custom';
  }
};

/**
 * The list the confirm panel renders. Every backend option is listed in
 * its backend order (SP → quotes → Std); the option equal to the resolved
 * price is flagged `recommended`. When the resolved price matches NO
 * option (base discount, hybrid backstop, options enumeration failed) it
 * is prepended as its own recommended row so the rep always sees the price
 * the line will get. Returns [] only when there is no resolved price.
 */
export const buildPriceChoices = (resolved: ResolvedPriceInfo): PriceChoice[] => {
  const options: PricingSourceOption[] = resolved.available_sources ?? [];
  const listed: PriceChoice[] = options.map((opt, i) => ({
    key: `${opt.type}-${opt.quote_source_id ?? i}`,
    type: opt.type,
    label: opt.label,
    sublabel: opt.sublabel,
    price: opt.price,
    recommended: opt.is_active,
  }));
  if (resolved.resolved_price == null) return listed;
  if (listed.some((c) => c.recommended)) return listed;
  const price = Number(resolved.resolved_price);
  if (!Number.isFinite(price)) return listed;
  return [
    {
      key: 'resolved',
      type: sourceTypeOf(resolved.source),
      label: resolvedSourceLabel(resolved),
      sublabel: resolved.product_hybrid_offered ?? null,
      price,
      recommended: true,
    },
    ...listed,
  ];
};

/** Index of the recommended choice, or null when nothing is recommended. */
export const recommendedChoiceIndex = (choices: PriceChoice[]): number | null => {
  const i = choices.findIndex((c) => c.recommended);
  return i === -1 ? null : i;
};

/**
 * Success line after the add, naming the price and where it came from —
 * "Added AU00249 × 1 @ $144.95 (Special Pricing)". `mode: 'set'` is the
 * "Set item code" path that updates an existing description-only line.
 */
export const formatAddSuccess = (
  itemCode: string,
  quantity: string,
  price: number,
  sourceLabel: string,
  mode: 'added' | 'set' = 'added',
): string => {
  const at = `@ $${price.toFixed(2)} (${sourceLabel})`;
  return mode === 'set'
    ? `Set ${itemCode} on existing line ${at}`
    : `Added ${itemCode} × ${quantity} ${at}`;
};

/**
 * Adapt a lookup's pricing classification to the `LineItemPricingContext`
 * shape `PricingSourcePicker` reads, for a line that is NOT yet on the
 * order (the Edit-in-Sage staged add). Only the picker's fields matter;
 * the rest are null so nothing downstream mistakes this for a saved line.
 */
export const pricingItemFromLookup = (
  itemCode: string,
  quantity: string,
  resolved: ResolvedPriceInfo,
): LineItemPricingContext => ({
  item_code: itemCode,
  item_description: null,
  quantity,
  unit_price: resolved.resolved_price,
  standard_price: null,
  ship_weight: null,
  udf_ship_wgt_lbs: null,
  special_price: null,
  special_price_note: null,
  has_special_pricing: (resolved.available_sources ?? []).some(
    (o) => o.type === 'special' || o.type === 'tiered' || o.type === 'base_discount',
  ),
  discount_pct: null,
  pricing_type: null,
  tier_price_1: null,
  tier_price_2: null,
  tier_price_3: null,
  tier_price_2_min: null,
  tier_price_3_min: null,
  resolved_price: resolved.resolved_price,
  price_source: resolved.source,
  price_source_detail: resolved.quote_id != null ? String(resolved.quote_id) : null,
  // Base-discount lookups: keep the % and list on the adapted line so a
  // staged re-pick (sageEditUtils.restagePricingItem) can rebuild the badge.
  applied_base_discount_pct: resolved.current_source?.base_discount_pct ?? null,
  list_price_before_discount:
    resolved.current_source?.list_price != null
      ? String(resolved.current_source.list_price)
      : null,
  current_source: resolved.current_source ?? null,
  available_sources: resolved.available_sources ?? [],
  extracted_price: null,
  has_po_price_mismatch: false,
  sheet_status: null,
  active_list_warning: false,
  expired_quote_match: null,
});
